import { FC } from 'react';
import { Box, Typography, Chip, Button, CircularProgress, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { usePrivileges } from '@/hooks/usePrivileges';
import { PrivilegeEnum, StatusDto } from '@/api/ApiClient';
import { STATUS_MAP } from '@/lib/types/statusTypes';
import type { EntityContextType } from '@/stores/authStore';
import { useBackend } from '@/hooks/useBackend';
import { useDeleteEntity } from '@/hooks/useDeleteEntity';
import { useSubmitEntity } from '@/hooks/useSubmitEntity';
import { useResetEntity } from '@/hooks/useResetEntity';
import { TreeNodeFactory } from '@/lib/tree/treeNodeFactory';

import { useUserResolution } from '@/lib/options/useUserResolution';
import { useTree } from '@/hooks/useTree';
import { useDataActions } from '@/hooks/useDataState';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified } from '@/hooks/useTreeDetailSubscriptions';

interface FooterProps {
  lastChangedDate?: string | Date;
  lastChangedBy?: string;
  status?: string;
  onSave: () => Promise<void>;
  isReadOnly: boolean;
  setIsReadOnly: (val: boolean) => void;
  privilege: PrivilegeEnum;
  isFormValid?: boolean;
  /** Optional custom submit handler. If provided, overrides default submit behavior */
  onSubmit?: () => Promise<boolean>;
  /**
   * When true, the Delete button is hidden.
   *  Temporary: used only for split activities for now until backend delete (ActivitySubSample FK) is fixed.
   */
  hideDelete?: boolean;
  /** When true, the Delete button is shown but disabled (e.g. sample has submitted activities). */
  disableDelete?: boolean;
  /** When true, hide Submit and Reset workflow actions regardless of status. */
  hideWorkflowActions?: boolean;
  /** When true, Delete visibility ignores status checks (e.g., allow delete even when submitted). */
  ignoreStatusForDelete?: boolean;
  /** Load merged privileges for this entity (getAccessRights); improves orphan/capped sample accuracy. */
  privilegeContext?: { entityType: EntityContextType; entityId: string };
  /** Server <c>canUpdateSample</c> when available; overrides update check for Edit/Save visibility. */
  canUpdate?: boolean;
}

export const Footer: FC<FooterProps> = ({
  lastChangedDate,
  lastChangedBy,
  status,
  onSave,
  isReadOnly,
  setIsReadOnly,
  privilege,
  isFormValid = true,
  onSubmit: customOnSubmit,
  hideDelete = false,
  disableDelete = false,
  hideWorkflowActions = false,
  ignoreStatusForDelete = false,
  privilegeContext,
  canUpdate,
}) => {
  const { isAuthorized } = usePrivileges(privilegeContext?.entityType, privilegeContext?.entityId);

  const isAuthorizedFor = (op: Parameters<typeof isAuthorized>[1]) => {
    if (op === 'update' && canUpdate !== undefined) {
      return canUpdate;
    }
    return isAuthorized(privilege, op);
  };
  const { resolveUser } = useUserResolution();
  const { reloadNode, setDataModified, getData } = useTree();
  const { selectedNode } = useTreeSelection();
  const isModified = useIsDataModified(selectedNode?.dataId);

  const formatStatus = (status: string | undefined) => {
    if (isModified) {
      return <Chip label="Unsaved Changes" color="error" size="small" icon={<WarningAmberIcon />} sx={{ ml: 1 }} />;
    }
    if (!status) return 'Not specified';

    const statusInfo = STATUS_MAP[status];
    const label = statusInfo?.label || status;
    const chipColor = statusInfo?.chipColor ?? 'default';

    return <Chip label={label} color={chipColor} size="small" icon={statusInfo?.icon} sx={{ ml: 1 }} />;
  };

  const formattedDate = lastChangedDate ? format(new Date(lastChangedDate), 'PPpp') : 'Unknown';

  const backend = useBackend();
  const { deleteEntity } = useDeleteEntity();
  const { submitEntity } = useSubmitEntity();
  const { resetEntity } = useResetEntity();

  // Subscribe to specific loading states for better performance
  const { isDeleting, isSubmitting, isResetting } = useDataActions(selectedNode?.dataId);

  // Check if any operation is in progress
  const isAnyOperationInProgress = isDeleting || isSubmitting || isResetting;

  const handleCancel = async () => {
    if (!selectedNode) return;

    const wasModified = isModified;

    // Exit edit mode FIRST to destroy recipe store and stop its sync reaction
    // (otherwise reload can trigger store creation / reaction and re-mark as modified)
    setIsReadOnly(true);

    if (wasModified) {
      setDataModified(selectedNode.dataId, false);
      await reloadNode(selectedNode.nodeId, backend);
    }
  };

  return (
    <Box
      sx={{
        borderTop: '1px solid #e0e0e0',
        mt: 3,
        pt: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        fontSize: '0.875rem',
        color: 'text.secondary',
      }}
    >
      <Typography variant="body1" component="div" sx={{ fontSize: '1rem', flex: 1, mr: 2 }}>
        Last modification on <strong>{formattedDate}</strong> by{' '}
        <strong>{lastChangedBy ? resolveUser(lastChangedBy) : 'Unknown'}</strong>.&nbsp; Current status:{' '}
        {formatStatus(status)}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        {/* 1. If Readonly → show Edit button */}
        {isReadOnly && isAuthorizedFor('update') ? (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            size="large"
            onClick={() => setIsReadOnly(false)}
            disabled={isAnyOperationInProgress}
          >
            Edit Details
          </Button>
        ) : null}

        {/* 2. If Modified → show Save button */}
        {!isReadOnly && isModified ? (
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={isAnyOperationInProgress || !isFormValid}
          >
            Save Changes
          </Button>
        ) : null}

        {/* 3. If Modified → show Undo button */}
        {!isReadOnly ? (
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<ClearIcon />}
            onClick={handleCancel}
            disabled={isAnyOperationInProgress}
          >
            Cancel
          </Button>
        ) : null}

        {/* 4. If NOT modified and status === Pending → show Submit button */}
        {!hideWorkflowActions && !isModified && status === StatusDto.Pending && isAuthorizedFor('submit')
          ? (() => {
              return (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={async () => {
                    if (!selectedNode) return;
                    // Use custom submit handler if provided, otherwise use default
                    if (customOnSubmit) {
                      await customOnSubmit();
                    } else {
                      await submitEntity(selectedNode);
                    }
                  }}
                  disabled={isAnyOperationInProgress}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              );
            })()
          : null}
        {/* 5. If NOT modified and status === Submitted → show Reset button */}
        {!hideWorkflowActions && status === StatusDto.Submitted && isAuthorizedFor('reset') ? (
          <Button
            variant="outlined"
            color="warning"
            size="large"
            onClick={async () => {
              if (!selectedNode) return;
              await resetEntity(selectedNode);
            }}
            disabled={isAnyOperationInProgress}
            startIcon={isResetting ? <CircularProgress size={20} color="inherit" /> : <RestartAltIcon />}
          >
            {isResetting ? 'Resetting...' : 'Reset to Pending'}
          </Button>
        ) : null}
        {/* Show Delete button only if status is not Submitted and hideDelete is not set (temporary: split activity) */}
        {!isModified &&
        (ignoreStatusForDelete || status !== StatusDto.Submitted) &&
        isAuthorizedFor('delete') &&
        !hideDelete ? (
          <Tooltip title={disableDelete ? 'Cannot delete: this sample has submitted activities' : ''}>
            <span>
              <Button
                variant="contained"
                color="error"
                size="large"
                onClick={async () => {
                  if (!selectedNode) return;
                  await deleteEntity(selectedNode);
                }}
                disabled={isAnyOperationInProgress || disableDelete}
                startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              >
                {isDeleting
                  ? 'Deleting...'
                  : `Delete ${selectedNode ? TreeNodeFactory.getNodeTypeLabel(selectedNode, getData) : ''}`}
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
};
